import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { outsourceInvoiceConfigByCode } from 'entities';

import { Button, Column, Row } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';

import {
  SettingsIcon,
  UserIcon,
  DollarBillIcon,
  UserSquareIcon,
  ChatIcon,
  FolderIcon,
  ExternalLinkIcon,
} from 'components/Icons';

import { formatCurrency } from 'utils/text';

import { InvoiceTabProps, OutsourceInvoiceWithProject } from './types';

const invoiceProps: Array<{
  getName: (t: TFunction) => string;
  extractor: (invoice: OutsourceInvoiceWithProject) => string | ReactNode;
  Icon: typeof SettingsIcon;
  shouldRender?: (invoice: OutsourceInvoiceWithProject) => boolean;
}> = [
  {
    getName: (t: TFunction) => t('service'),
    extractor: ({ serviceName }) => serviceName,
    Icon: SettingsIcon,
  },
  {
    getName: (t: TFunction) => t('value'),
    shouldRender: ({ value }) => !!value,
    Icon: DollarBillIcon,
    extractor: ({ currency, value }) => {
      return (
        <Row gap="1.5">
          <span className="text-success-light">{currency}</span>

          <span>{formatCurrency(value as string)}</span>
        </Row>
      );
    },
  },
  {
    getName: (t: TFunction) => t('provider'),
    extractor: ({ providerName }) => providerName,
    Icon: UserIcon,
  },
  {
    getName: (t: TFunction) => t('provider-cost'),
    Icon: UserSquareIcon,
    extractor: ({ project, serviceConfigId, status, providerCost }) => {
      const cost = outsourceInvoiceConfigByCode[status].providerCostIsFinal
        ? providerCost
        : project.services.find(({ id }) => id === serviceConfigId)?.outsourceConfig?.providerCost;

      return `${cost?.currency} ${formatCurrency(cost?.value ?? '')}`;
    },
  },
  {
    getName: (t: TFunction) => t('cost-to-client'),
    Icon: DollarBillIcon,
    extractor: ({ project, serviceConfigId }) => {
      const cost = project.services.find(({ id }) => id === serviceConfigId)?.outsourceConfig
        ?.clientCost;

      return `${cost?.currency} ${formatCurrency(cost?.value ?? '')}`;
    },
  },
  {
    getName: (t: TFunction) => t('justification'),
    extractor: ({ justification }) => justification || 'N/A',
    Icon: ChatIcon,
  },
  {
    getName: (t: TFunction) => t('project'),
    Icon: FolderIcon,
    extractor: ({ project }) => (
      <Button
        to="projectProfile"
        params={{ projectId: project.id }}
        linkTarget={project.id}
        variant="ghost"
        className="-mt-2"
      >
        {project.name}
        <ExternalLinkIcon size={18} />
      </Button>
    ),
  },
];

export function InvoiceMainData({ invoice }: InvoiceTabProps): JSX.Element {
  const { t } = useLang();

  return (
    <Column>
      {invoiceProps.map(
        ({ Icon, getName, extractor, shouldRender }) =>
          (shouldRender?.(invoice) ?? true) && (
            <PropertyWithIcon
              key={`invoice-info-${getName(t)}`}
              icon={<Icon />}
              name={getName(t)}
              value={extractor(invoice)}
            />
          ),
      )}
    </Column>
  );
}
